import {
  BehaviorSubject,
  catchError,
  firstValueFrom,
  map,
  Observable,
  of,
} from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { environment } from '../../../../environments/environment';
import { AuthAnswer } from '../../models/auth-answer';
import { AuthTokens } from '../../models/auth-tokens';
import { TokenService } from '../token/token.service';

@Injectable({
  providedIn: 'root',
  deps: [HttpClient, TokenService, Router],
})
export class AuthService {
  /**
   * Stores the state of the authentication tokens
   */
  private auth$: BehaviorSubject<AuthTokens | null> =
    new BehaviorSubject<AuthTokens | null>(null);

  /**
   * Observable that contains the state of the tokens
   */
  public readonly auth: Observable<AuthTokens | null> =
    this.auth$.asObservable();

  constructor(
    private http: HttpClient,
    private tokenService: TokenService,
    private router: Router
  ) {}

  /**
   * Sends the credentials to the server
   */
  public async authUser(
    email: string,
    password: string
  ): Promise<AuthAnswer | null> {
    try {
      const url = `${environment.incopescaDigitalService.apiServer}${environment.endpoints.login}`;
      const authAnswer = await firstValueFrom(
        this.http.post<AuthAnswer>(
          url,
          { email, password },
          { headers: { app_name: 'platform' } }
        )
      );

      if (authAnswer) {
        const tokens: AuthTokens = {
          uuid: authAnswer.user.uuid,
          token: authAnswer.accessToken,
          expirationDate: authAnswer.exp,
          roles: authAnswer.roles,
        };
        this.tokenService.saveData(authAnswer);
        this.auth$.next(tokens);
        return authAnswer;
      } else {
        console.error('Invalid credentials');
        throw new Error('Invalid credentials');
      }
    } catch (error) {
      console.error('Authentication error:', error);
      throw new Error('Invalid credentials');
    }
  }

  /**
   * Get Current Value of the tokens
   */
  public getCurrentTokens(): AuthTokens | null {
    return this.auth$.getValue();
  }

  public forgotPassword(email: string) {
    return this.http
      .post(
        environment.incopescaDigitalService.apiServer +
          environment.endpoints.forgottenPassword,
        { email }
      )
      .pipe(catchError(() => of(false)));
  }

  public resetPassword(password: string, token: string): Observable<boolean> {
    return this.http
      .post(
        environment.incopescaDigitalService.apiServer +
          environment.endpoints.resetPassword,
        { password, token }
      )
      .pipe(
        map(() => true),
        catchError(() => {
          return of(false);
        })
      );
  }

  public refreshToken(refreshToken: string) {
    return this.http.post<AuthAnswer>(
      environment.incopescaDigitalService.apiServer +
        environment.endpoints.refreshToken,
      { refreshToken }
    );
  }

  public clearAuthData(): void {
    sessionStorage.clear();
    this.auth$.next(null);
  }

  public logout(): void {
    this.clearAuthData();
    this.router.navigate(['/login']);
  }

  public emailConfirmation(token: string) {
    const url = `${environment.incopescaDigitalService.apiServer}${environment.endpoints.emailConfirmation}/${token}`;
    return this.http.get<boolean>(url).pipe(
      catchError(() => {
        return of(false);
      })
    );
  }
}
